import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
  useMapEvents,
  ZoomControl,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L, { LatLng, LatLngBounds } from "leaflet";
import { placeholder, workerInPinIcon, workerOutPinIcon } from "../assets/imgs";
import { useEffect, useState, useRef } from "react";

export default function Maps(props: {
  selectPosition: any;
  setSelectPosition: Function;
  zoom: any;
  status?: any;
  workerCount?: any;
  scrollWheelZoom?: L.Zoom;
  showGasGPS?: boolean;
  showWatchGPS?: boolean;
}) {
  const {
    selectPosition,
    setSelectPosition,
    zoom,
    status,
    workerCount,
    showGasGPS,
    showWatchGPS,
  } = props;
  const [position, setPosition] = useState<{ lat: number; lng: number } | null>(
    null
  );
  const mapRef = useRef<L.Map | null>(null);
  const count = useRef(1);
  const workerHasWatch = useRef(1);

  // Reset counters when status changes
  useEffect(() => {
    console.log("Resetting worker counters");
    count.current = 1;
    workerHasWatch.current = 1;
  }, [status]);

  // Initialize position from worker status
  useEffect(() => {
    console.log("=== MapsForEntry Debug ===");
    console.log(
      "Initializing map position with worker status:",
      selectPosition
    );
    if (Array.isArray(selectPosition) && selectPosition.length > 0) {
      console.log("Found workers with positions:", selectPosition.length);
      const workerWithGPS = selectPosition.find((worker: any) => {
        // Check if worker has position data
        const hasPosition =
          worker?.position &&
          typeof worker.position === "object" &&
          worker.position !== null;

        // Check if position has valid lat/lng
        const hasValidPosition =
          hasPosition &&
          worker.position.lat !== undefined &&
          worker.position.lng !== undefined;

        // Check if worker has valid type
        const hasValidType =
          Array.isArray(worker?.realTimeResourceTypeId) &&
          worker.realTimeResourceTypeId.length > 0;

        console.log("Checking worker:", {
          name: worker?.name,
          position: worker?.position,
          type: worker?.realTimeResourceTypeId,
          hasPosition,
          hasValidPosition,
          hasValidType,
        });

        return hasValidPosition && hasValidType;
      });

      if (workerWithGPS) {
        const lat = parseFloat(workerWithGPS.position.lat);
        const lng = parseFloat(workerWithGPS.position.lng);
        console.log("Found worker with GPS:", {
          name: workerWithGPS.name,
          position: { lat, lng },
          isValid: !isNaN(lat) && !isNaN(lng),
        });
        if (!isNaN(lat) && !isNaN(lng)) {
          console.log("Setting map position to:", { lat, lng });
          setPosition({ lat, lng });
        } else {
          console.warn(
            "Invalid coordinates for worker:",
            workerWithGPS.name,
            workerWithGPS.position
          );
        }
      } else {
        console.warn("No worker found with valid GPS data");
      }
    } else {
      console.warn("No workers available or invalid worker status format");
    }
  }, [selectPosition]);

  const getPinIcon = (worker: any) => {
    const isIn =
      worker?.noOfEntries[0]?.timeIn && !worker?.noOfEntries[0]?.timeOut;
    const iconUrl = isIn ? workerInPinIcon : workerOutPinIcon;
    const label =
      worker?.type === 1
        ? "SRP"
        : worker?.type === 2
        ? "SP"
        : `W${count.current++}`;

    return L.divIcon({
      html: `
                <div style="position: relative;">
                    <img src="${iconUrl}" style="position: absolute; top: 50%; left: 40%; transform: translate(-50%, -40%); display: block;">
                    <span style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -80%); color: black; font-weight: bold; background-color: transparent; padding: 4px;">
                        ${label}
                    </span>
                </div>
            `,
    });
  };

  const getWatchPinIcon = (worker: any) => {
    const isIn =
      worker?.noOfEntries[0]?.timeIn && !worker?.noOfEntries[0]?.timeOut;
    const iconUrl = isIn ? workerInPinIcon : workerOutPinIcon;
    const label =
      worker?.type === 1
        ? "SRP/M"
        : worker?.type === 2
        ? "SP/M"
        : `W${workerHasWatch.current++}/M`;

    return L.divIcon({
      html: `
                <div style="position: relative;">
                    <img src="${iconUrl}" style="position: absolute; top: 50%; left: 40%; transform: translate(-50%, -40%); display: block;">
                    <span style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -80%); color: black; font-weight: bold; background-color: transparent; padding: 4px;">
                        ${label}
                    </span>
                </div>
            `,
    });
  };

  const placeholderIcon = L.icon({
    iconUrl: placeholder,
    iconSize: [38, 38],
  });

  const worker1Icon = L.icon({
    iconUrl: workerInPinIcon,
    iconSize: [38, 38],
  });

  const Recenter = ({ lat, lng }: { lat: number; lng: number }) => {
    const map = useMap();
    useEffect(() => {
      console.log("=== Recenter Debug ===");
      console.log("Attempting to recenter map:", { lat, lng });
      if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
        console.log("Recenter valid, setting view to:", { lat, lng });
        map.setView([lat, lng], map.getZoom());
        mapRef.current = map;
      } else {
        console.warn("Invalid coordinates for recenter:", { lat, lng });
      }
    }, [lat, lng]);
    return null;
  };

  // Ensure we have valid coordinates for the map center
  const mapCenter =
    position && position.lat !== undefined && position.lng !== undefined
      ? position
      : { lat: 22.3351172, lng: 114.177216 };

  return (
    <MapContainer
      center={mapCenter}
      zoom={17}
      style={{ width: "100%", height: "100%" }}
      dragging={false}
      scrollWheelZoom={props.scrollWheelZoom ?? false}
      doubleClickZoom={false}
    >
      {position && position.lat !== undefined && position.lng !== undefined && (
        <Recenter lat={position.lat} lng={position.lng} />
      )}
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://api.maptiler.com/maps/basic-v2/256/{z}/{x}/{y}.png?key=mNVo7cfW5z37vOy84x8P"
      />
      {Array.isArray(status) &&
        status
          .filter((x) => x.realTimeResourceTypeId?.includes(1))
          .map(
            (worker: any) =>
              worker?.showGasGPS &&
              worker?.position?.lat !== undefined &&
              worker?.position?.lng !== undefined && (
                <Marker
                  key={worker?.id}
                  icon={getPinIcon(worker)}
                  position={[worker?.position?.lat, worker?.position?.lng]}
                  eventHandlers={{
                    click: () => {
                      if (worker) setSelectPosition(worker);
                    },
                  }}
                ></Marker>
              )
          )}
      {Array.isArray(status) &&
        status
          .filter((x) => x.realTimeResourceTypeId?.includes(2))
          .map((worker: any) => {
            const watchLat =
              worker?.watchLatitude !== undefined
                ? worker.watchLatitude
                : position?.lat;
            const watchLng =
              worker?.watchLongitude !== undefined
                ? worker.watchLongitude
                : position?.lng;

            return (
              worker?.showWatchGPS &&
              watchLat !== undefined &&
              watchLng !== undefined && (
                <Marker
                  key={"watch/" + worker?.id}
                  icon={getWatchPinIcon(worker)}
                  position={[watchLat, watchLng]}
                  eventHandlers={{
                    click: () => {
                      if (worker) setSelectPosition(worker);
                    },
                  }}
                ></Marker>
              )
            );
          })}
    </MapContainer>
  );
}
